"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  nextFrame,
  useInitializedEffect,
  useOnce,
  useDebug,
} from "@huub-web/ui";
import { isWindowDefined } from "@huub-web/utils";

import { useWebSocketListener } from "./useWebSocketListener";
import {
  type WebSocketAudioBlobMessage,
  type WebSocketAudioEndMessage,
  type WebSocketAudioStartMessage,
  WebSocketChannel,
} from "../../types";

const AUDIO_MIME_TYPE = "audio/mpeg";
const MIN_BUFFERS_TO_PLAY = 10;

/**
 * Hook to handle audio playback using MediaSource for dynamic streaming.
 * @todo Try to re-use already instatiated objects if possible, and not reset each start
 * @returns The audio streaming state.
 */
export const useAudioStreaming = (
  enabled: boolean = true,
  systemEnabled: boolean = false,
) => {
  const { log } = useDebug();

  const audioContext = useRef<AudioContext | null>(null);
  const mediaSource = useRef<MediaSource | null>(null);
  const sourceBuffer = useRef<SourceBuffer | null>(null);
  const audioElement = useRef<HTMLAudioElement | null>(null);
  const fileReader = useRef<FileReader | null>(null);
  const pendingBuffers = useRef<ArrayBuffer[]>([]);
  const audioSource = useRef<MediaElementAudioSourceNode | null>(null);
  const gainNode = useRef<GainNode | null>(null);
  const blobQueue = useRef<Blob[]>([]);
  const analyserNode = useRef<AnalyserNode | null>(null);
  const volumeDataArray = useRef<Uint8Array | null>(null);

  const hasInitialData = useRef(false);
  const isMediaSourceOpen = useRef(false);
  const processedBuffers = useRef(0);
  const isProcessing = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const manuallyStopped = useRef(false);
  const hasStreamEnded = useRef(false);

  const [isAudioContextRunning, setIsAudioContextRunning] = useState(false);

  useEffect(() => {
    if (!systemEnabled) {
      return;
    }

    log("orange", "useAudioStreaming", "Enabled", enabled);
    if (audioElement.current) {
      audioElement.current.volume = enabled ? 1 : 0;
    }
    if (!enabled) {
      stopPlaying();
    }
  }, [enabled, systemEnabled]);

  /**
   * We need to manually start the audio context when the user clicks the page.
   */

  const handleClick = useCallback(() => {
    if (!systemEnabled) {
      return;
    }

    initAudioContext();
    initAnalyser();
    initMediaSource();
    initFileReader();
  }, [systemEnabled]);

  useEffect(() => {
    if (!systemEnabled || !isWindowDefined()) {
      return;
    }
    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, [systemEnabled, handleClick]);

  useOnce(() => {
    if (!systemEnabled) {
      return;
    }

    initAudioContext();
  }, []);

  /**
   * WebSocket event listeners for audio streaming.
   */

  useWebSocketListener<WebSocketAudioStartMessage>(
    WebSocketChannel.AUDIO_START,
    () => {
      if (!audioContext.current || audioContext.current.state !== "running") {
        return;
      }

      log("orange", "useAudioStreaming", "Initializing");
      manuallyStopped.current = false;

      reset();

      initAnalyser();
      initMediaSource();
      initFileReader();
    },
  );

  useWebSocketListener<WebSocketAudioBlobMessage>(
    WebSocketChannel.AUDIO_BLOB,
    (message) => {
      if (
        !audioContext.current ||
        audioContext.current.state !== "running" ||
        manuallyStopped.current
      ) {
        return;
      }

      nextFrame(() => {
        appendToBlobQueue(message.data);
      });
    },
  );

  useWebSocketListener<WebSocketAudioEndMessage>(
    WebSocketChannel.AUDIO_END,
    () => {
      if (!audioContext.current || audioContext.current.state !== "running") {
        return;
      }

      nextFrame(() => {
        if (isPlaying || isProcessing.current || hasStreamEnded.current) {
          hasStreamEnded.current = true;
          maybeEndPlayback();
        }
      });
    },
  );

  /**
   * Handle source buffer update event to append pending buffers.
   */
  const handleSourceBufferUpdate = () => {
    // console.log("Source buffer update event, buffer state:", {
    //   updating: sourceBuffer.current?.updating,
    //   pendingBuffers: pendingBuffers.current.length,
    //   readyState: mediaSource.current?.readyState,
    //   processedBuffers: processedBuffers.current,
    //   pendingBlobs: pendingBlobs.current.length,
    // });

    if (sourceBuffer.current && !sourceBuffer.current.updating) {
      processedBuffers.current++;

      // Start playback after enough data and keep processing
      if (
        !hasInitialData.current &&
        audioElement.current &&
        processedBuffers.current >= MIN_BUFFERS_TO_PLAY
      ) {
        hasInitialData.current = true;
        playAudio();
      }

      if (pendingBuffers.current.length > 0) {
        const nextBuffer = pendingBuffers.current.shift();
        if (nextBuffer) {
          try {
            sourceBuffer.current.appendBuffer(nextBuffer);
          } catch (error) {
            console.error("Error appending buffer:", error);
            pendingBuffers.current.unshift(nextBuffer);
          }
        }
      }
    }
  };

  /**
   * Check if stream has ended and close media source if needed.
   */
  const maybeEndPlayback = () => {
    // console.log("\x1b[32m%s\x1b[0m", "[useAudioStreaming] maybeEndPlayback", {
    //   pendingBlobs: pendingBlobs.current.length,
    //   hasStreamEnded: hasStreamEnded.current,
    //   mediaSourceReady: !!mediaSource.current,
    // });

    if (
      blobQueue.current.length === 0 &&
      hasStreamEnded.current &&
      mediaSource.current &&
      mediaSource.current.readyState === "open"
    ) {
      log("orange", ["useAudioStreaming", "maybeEndPlayback"], "Ending stream");

      nextFrame(() => {
        if (!mediaSource.current || mediaSource.current.readyState !== "open") {
          return false;
        }
        mediaSource.current.endOfStream();
      });
    }
  };

  /**
   * Process next blob in queue if available and not already processing.
   */
  const processNextBlob = () => {
    log(
      "orange",
      ["useAudioStreaming", "processNextBlob"],
      "Checking next blob",
      {
        processing: isProcessing.current,
        pendingBlobs: blobQueue.current.length,
        fileReaderReady: !!fileReader.current,
      },
    );

    if (
      isProcessing.current ||
      blobQueue.current.length === 0 ||
      !fileReader.current
    ) {
      maybeEndPlayback();
      return;
    }

    const nextBlob = blobQueue.current.shift();
    if (nextBlob) {
      try {
        isProcessing.current = true;
        log(
          "red",
          ["useAudioStreaming", "processNextBlob"],
          "⎿ Processing next blob, remaining:",
          blobQueue.current.length,
        );

        fileReader.current.readAsArrayBuffer(nextBlob);
      } catch (error) {
        console.error("Error reading blob:", error);

        blobQueue.current.unshift(nextBlob);
        isProcessing.current = false;
      }
    } else {
      log(
        "red",
        ["useAudioStreaming", "processNextBlob"],
        "⎿ No blobs to process",
      );
    }
  };

  /**
   * Initialize audio context and nodes for audio processing.
   */
  function initAudioContext() {
    // console.log(
    //   "[useAudioStreaming] initAudioContext",
    //   audioContext.current?.state,
    // );

    if (audioContext.current) {
      setIsAudioContextRunning(audioContext.current.state === "running");

      if (audioContext.current.state === "running") {
        return;
      } else if (audioContext.current.state === "suspended") {
        audioContext.current.resume();
        return;
      } else {
        console.warn(
          "[useAudioStreaming] initAudioContext",
          "Audio context is in an unexpected state:",
          audioContext.current.state,
        );
        return;
      }
    }

    audioContext.current = new AudioContext();

    setIsAudioContextRunning(audioContext.current.state === "running");

    audioContext.current.addEventListener("statechange", () => {
      setIsAudioContextRunning(audioContext.current?.state === "running");
    });

    log(
      "red",
      ["useAudioStreaming", "initAudioContext"],
      "Audio initialized with state:",
      audioContext.current?.state,
    );

    if (audioContext.current.state === "suspended") {
      audioContext.current.resume();
    }
  }

  function initAnalyser() {
    if (!audioContext.current) {
      return;
    }

    // Create and configure gain node
    if (!gainNode.current) {
      gainNode.current = audioContext.current.createGain();
      gainNode.current.gain.value = 1.0; // Adjust volume as needed
      gainNode.current.connect(audioContext.current.destination);
    }

    // Create and configure analyser node
    if (!analyserNode.current) {
      analyserNode.current = audioContext.current.createAnalyser();
      analyserNode.current.fftSize = 256;
      volumeDataArray.current = new Uint8Array(
        analyserNode.current.frequencyBinCount,
      );

      // Connect nodes: source -> gain -> analyser -> destination
      gainNode.current.connect(analyserNode.current);
      analyserNode.current.connect(audioContext.current.destination);
    }
  }

  /**
   * Initialize file reader for reading audio blobs and appending to source buffer.
   */
  function initFileReader() {
    if (fileReader.current) {
      return;
    }

    fileReader.current = new FileReader();
    fileReader.current.onload = () => {
      const arrayBuffer = fileReader.current?.result as ArrayBuffer;
      if (sourceBuffer.current) {
        if (!sourceBuffer.current.updating) {
          try {
            sourceBuffer.current.appendBuffer(arrayBuffer);
          } catch (error) {
            console.error("Error appending buffer directly:", error);
            pendingBuffers.current.push(arrayBuffer);
          }
        } else {
          pendingBuffers.current.push(arrayBuffer);
        }
      }
      isProcessing.current = false;
      nextFrame(processNextBlob);
    };
    fileReader.current.onloadend = () => {
      isProcessing.current = false;
      nextFrame(processNextBlob);
    };
    fileReader.current.onerror = (error) => {
      console.error("Error reading audio blob:", error);
      isProcessing.current = false;
      nextFrame(processNextBlob);
    };
  }

  /**
   * Initialize MediaSource and audio element for streaming.
   */
  function initMediaSource() {
    try {
      mediaSource.current = new MediaSource();

      audioElement.current = new Audio();
      audioElement.current.crossOrigin = "anonymous"; // Important for audio context
      audioElement.current.src = URL.createObjectURL(mediaSource.current);
      audioElement.current.volume = enabled ? 1 : 0;
      audioElement.current.addEventListener("ended", () => endAudio());

      mediaSource.current.addEventListener("sourceopen", () => {
        isMediaSourceOpen.current = true;
        // console.log("MediaSource opened");

        if (mediaSource.current && audioContext.current) {
          try {
            sourceBuffer.current =
              mediaSource.current.addSourceBuffer(AUDIO_MIME_TYPE);
            sourceBuffer.current.addEventListener(
              "update",
              handleSourceBufferUpdate,
            );

            // Connect audio element to audio context
            if (audioElement.current && !audioSource.current) {
              audioSource.current =
                audioContext.current.createMediaElementSource(
                  audioElement.current,
                );
              if (gainNode.current) {
                audioSource.current.connect(gainNode.current);
              }
            }
          } catch (error) {
            console.error("Error creating source buffer:", error);
          }
        }
      });
      mediaSource.current.addEventListener("sourceended", () => {
        isMediaSourceOpen.current = false;
      });
    } catch (error) {
      console.error("Error initializing MediaSource:", error);
    }
  }

  /**
   * Append audio blob to queue for processing.
   */
  function appendToBlobQueue(data: Blob) {
    if (manuallyStopped.current) {
      return;
    }

    log("orange", ["useAudioStreaming", "appendToBlobQueue"], {
      size: data.size,
      mediaSourceReady: isMediaSourceOpen.current,
      sourceBufferReady: !!sourceBuffer.current,
      readyState: fileReader.current?.readyState,
      pendingBlobs: blobQueue.current.length,
    });

    if (!isMediaSourceOpen.current || !sourceBuffer.current) {
      console.warn("MediaSource not ready or SourceBuffer not initialized.");
      return;
    }

    blobQueue.current.push(data);

    // Try to process immediately if possible
    if (fileReader.current /* && !fileReader.current.readyState*/) {
      processNextBlob();
    } else {
      log(
        "red",
        ["useAudioStreaming", "appendToBlobQueue"],
        "⎿ ⚠️ fileReader not ready",
      );
    }
  }

  /**
   * Start audio playback with current buffers.
   */
  const playAudio = async () => {
    // console.log(
    //   "[useAudioStreaming] playAudio - Starting playback with",
    //   processedBuffers.current,
    //   "buffers processed",
    // );

    try {
      await audioContext.current?.resume();
      if (audioElement.current) {
        // audioElement.current.volume = 1.0;
        await audioElement.current.play();
        // const playResult =
        // console.log("Playback started", playResult);

        log("orange", "useAudioStreaming", "▶️ Playback started");
        setIsPlaying(true);
      } else {
        log("orange", "useAudioStreaming", "⚠️ Audio element not initialized");
      }
    } catch (error) {
      console.error("Error playing audio:", error);
    }
  };

  /**
   * End audio playback.
   */
  const endAudio = () => {
    log("orange", "useAudioStreaming", "Playback ended");
    setIsPlaying(false);
  };

  /**
   * Reset all audio streaming objects and state to initial values.
   */
  const reset = () => {
    log("orange", "useAudioStreaming", "Reset");

    hasStreamEnded.current = false;
    processedBuffers.current = 0;
    blobQueue.current = [];
    pendingBuffers.current = [];
    isProcessing.current = false;
    fileReader.current = null;
    hasInitialData.current = false;
    isMediaSourceOpen.current = false;
    volumeDataArray.current = null;

    setIsPlaying(false);
    setAnalysedVolume(0);
    lastAnalysedVolume.current = 0;

    if (gainNode.current) {
      gainNode.current.disconnect();
      gainNode.current = null;
    }
    if (analyserNode.current) {
      analyserNode.current.disconnect();
      analyserNode.current = null;
    }

    // if (audioContext.current) {
    //   audioContext.current.close();
    //   audioContext.current = null;
    // }

    if (sourceBuffer.current) {
      sourceBuffer.current.removeEventListener(
        "update",
        handleSourceBufferUpdate,
      );
    }
    if (audioSource.current) {
      audioSource.current.disconnect();
      audioSource.current = null;
    }
    if (mediaSource.current) {
      mediaSource.current = null;
    }
    if (audioElement.current) {
      audioElement.current.pause();
      audioElement.current.src = "";
      audioElement.current = null;
    }
  };

  useInitializedEffect(() => () => reset(), []);

  const stopPlaying = () => {
    if (audioElement.current) {
      audioElement.current.pause();
    }
    manuallyStopped.current = true;
    reset();
  };

  // Audio analysis

  const getAnalysedVolume = (): number => {
    if (!analyserNode.current || !volumeDataArray.current) {
      return 0;
    }

    // @ts-ignore - Uint8Array generic typing varies between TS versions
    analyserNode.current.getByteFrequencyData(volumeDataArray.current);

    // Calculate average volume from frequency data
    const average =
      volumeDataArray.current.reduce(
        (acc: number, val: number) => acc + val,
        0,
      ) / volumeDataArray.current.length;

    return average;
  };

  const lastAnalysedVolume = useRef(0);
  const [analysedVolume, setAnalysedVolume] = useState<number>(0);

  useEffect(() => {
    if (!isPlaying || !enabled) {
      return;
    }

    let frame: number | null = null;

    const monitorVolume = () => {
      const volume = getAnalysedVolume();
      setAnalysedVolume(volume);
      frame = nextFrame(monitorVolume);
    };

    monitorVolume();

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [isPlaying]);

  useEffect(() => {
    if (
      analysedVolume > 12 &&
      Math.abs(analysedVolume - lastAnalysedVolume.current) > 20
    ) {
      const event = new CustomEvent("avatarAudioEvent", {
        detail: { volume: analysedVolume },
        bubbles: true,
        cancelable: true,
        composed: false,
      });

      window.dispatchEvent(event);

      lastAnalysedVolume.current = analysedVolume;
    }
  }, [analysedVolume]);

  return { isPlaying, analysedVolume, isAudioContextRunning, stopPlaying };
};
